import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  BookmarkIcon,
  ArrowsRightLeftIcon,
  ArrowUturnLeftIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  MagnifyingGlassIcon,
  UserMinusIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { AttendanceStatus } from '../../types/event';
import { FormationTemplate, Match, MatchSelectionRole, SelectablePlayer } from '../../types/match';
import { formationTemplatesAPI, matchesAPI } from '../../services/api';
import FormationPitch, { Jersey, PitchPlayer, PlayerToken } from './FormationPitch';
import {
  FORMATIONS,
  PitchSlot,
  ROLE_LABELS,
  TEAM_SIZES,
  applyFormation,
  applySlots,
  defaultFormationFor,
  formationSize,
  newSlotId,
  shortName,
  matchFormation,
  slotsFromLineup,
} from './formations';
import { RESPONSE_BADGES } from './responseBadges';
import { ShirtSuggestionMap, fillShirts, suggestShirt } from './shirtSuggestions';

interface EditorState {
  formation: string;
  slots: PitchSlot[];
  /** Cserék sorrendben. */
  bench: number[];
  shirts: Record<number, number | undefined>;
  /** A cserék posztja (pl. cserekapus); a pályán lévőké a helyükhöz tartozik. */
  benchRoles: Record<number, string | undefined>;
}

type Source = { kind: 'slot'; slotId: string } | { kind: 'bench'; playerId: number } | { kind: 'pool'; playerId: number };
type Selected = { kind: 'slot'; slotId: string } | { kind: 'bench'; playerId: number } | null;

interface DragState {
  source: Source;
  playerId: number;
  role?: string;
  startX: number;
  startY: number;
  active: boolean;
}

const DRAG_THRESHOLD = 6;
const MAX_HISTORY = 50;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const buildInitialState = (match: Match): EditorState => {
  const { formation, slots } = slotsFromLineup(
    matchFormation(match),
    match.lineup.map((l) => ({ playerId: l.playerId, position: l.position, pitchX: l.pitchX, pitchY: l.pitchY }))
  );
  const starterIds = new Set(match.lineup.map((l) => l.playerId));
  const bench = [...match.subs].sort((a, b) => a.subOrder - b.subOrder).map((s) => s.playerId);
  // Régi adat: aki csak a keretben szerepel, az a cserepadra kerül
  match.squad.forEach((sq) => {
    if (!starterIds.has(sq.playerId) && !bench.includes(sq.playerId)) bench.push(sq.playerId);
  });
  const shirts: Record<number, number | undefined> = {};
  match.squad.forEach((s) => { shirts[s.playerId] = s.shirtNumber; });
  match.subs.forEach((s) => { if (s.shirtNumber != null) shirts[s.playerId] = s.shirtNumber; });
  match.lineup.forEach((l) => { if (l.shirtNumber != null) shirts[l.playerId] = l.shirtNumber; });
  const benchRoles: Record<number, string | undefined> = {};
  match.subs.forEach((s) => { if (s.position) benchRoles[s.playerId] = s.position; });
  return { formation, slots, bench, shirts, benchRoles };
};

// --- Tiszta állapot-műveletek ------------------------------------------------

const placeIntoSlot = (s: EditorState, src: Source, slotId: string): EditorState => {
  const slots = s.slots.map((x) => ({ ...x }));
  const bench = [...s.bench];
  const target = slots.find((x) => x.id === slotId);
  if (!target) return s;
  const occupant = target.playerId;

  if (src.kind === 'slot') {
    if (src.slotId === slotId) return s;
    const from = slots.find((x) => x.id === src.slotId);
    if (!from) return s;
    // Két pályán lévő játékos helyet cserél (üres helyre egyszerűen átkerül)
    target.playerId = from.playerId;
    from.playerId = occupant;
  } else {
    const p = src.playerId;
    if (occupant === p) return s;
    slots.forEach((x) => { if (x.playerId === p) x.playerId = undefined; });
    target.playerId = p;
    const benchIdx = bench.indexOf(p);
    // A lecserélt játékos a beálló helyére ül a padon
    if (benchIdx >= 0) bench.splice(benchIdx, 1, ...(occupant != null ? [occupant] : []));
    else if (occupant != null) bench.push(occupant);
    return { ...s, slots, bench, benchRoles: benchedRoles(s, [occupant]) };
  }
  return { ...s, slots, bench };
};

// A padra kerülő kapus kapus marad (cserekapus), a többiek poszt nélkül ülnek le
const benchedRoles = (s: EditorState, playerIds: (number | undefined)[]): Record<number, string | undefined> => {
  const roles = { ...s.benchRoles };
  playerIds.forEach((id) => {
    if (id == null) return;
    const slot = s.slots.find((x) => x.playerId === id);
    if (slot?.role === 'GK') roles[id] = 'GK';
  });
  return roles;
};

const sendToBench = (s: EditorState, playerId: number): EditorState => ({
  ...s,
  slots: s.slots.map((x) => (x.playerId === playerId ? { ...x, playerId: undefined } : x)),
  bench: s.bench.includes(playerId) ? s.bench : [...s.bench, playerId],
  benchRoles: benchedRoles(s, [playerId]),
});

const removePlayer = (s: EditorState, playerId: number): EditorState => ({
  ...s,
  slots: s.slots.map((x) => (x.playerId === playerId ? { ...x, playerId: undefined } : x)),
  bench: s.bench.filter((p) => p !== playerId),
});

const moveSlot = (s: EditorState, slotId: string, x: number, y: number): EditorState => ({
  ...s,
  slots: s.slots.map((slot) => (slot.id === slotId ? { ...slot, x: clamp(x, 4, 96), y: clamp(y, 3, 97) } : slot)),
});

const nearestSlot = (slots: PitchSlot[], x: number, y: number, onlyEmpty: boolean): PitchSlot | null => {
  let best: PitchSlot | null = null;
  let bestDist = Infinity;
  slots.forEach((s) => {
    if (onlyEmpty && s.playerId != null) return;
    const d = Math.hypot(s.x - x, s.y - y);
    if (d < bestDist) {
      best = s;
      bestDist = d;
    }
  });
  return best;
};

const sameState = (a: EditorState, b: EditorState) => JSON.stringify(a) === JSON.stringify(b);

// -----------------------------------------------------------------------------

interface LineupEditorProps {
  match: Match;
  eventTitle: string;
  eventAgeGroup: string;
  selectablePlayers: SelectablePlayer[];
  responses: Map<number, AttendanceStatus>;
  /** Mezszám-javaslatok a korosztály korábbi meccseiből. */
  shirtSuggestions?: ShirtSuggestionMap;
  onSaved: (match: Match) => void;
  onClose: () => void;
}

const NO_SUGGESTIONS: ShirtSuggestionMap = new Map();

/**
 * Felállás-szerkesztő: felállás választás, a játékosok helyre húzása vagy két
 * koppintással cseréje, cserepad és nevezés egy helyen. Teljes képernyőn nyílik,
 * egérrel és érintéssel is kezelhető.
 */
const LineupEditor: React.FC<LineupEditorProps> = ({
  match,
  eventTitle,
  eventAgeGroup,
  selectablePlayers,
  responses,
  shirtSuggestions = NO_SUGGESTIONS,
  onSaved,
  onClose,
}) => {
  const initial = useMemo(() => buildInitialState(match), [match]);
  const [state, setState] = useState<EditorState>(initial);
  const [past, setPast] = useState<EditorState[]>([]);
  const [selected, setSelected] = useState<Selected>(null);
  // Csere mód: a kijelölt játékos a következő koppintott helyre / játékossal cserél
  const [swapMode, setSwapMode] = useState(false);
  const [picker, setPicker] = useState<{ slotId?: string } | null>(null);
  const [ghost, setGhost] = useState<{ playerId: number; role?: string; x: number; y: number } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pitchSize, setPitchSize] = useState<{ w: number; h: number } | null>(null);
  const [templates, setTemplates] = useState<FormationTemplate[]>([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  const stateRef = useRef(state);
  stateRef.current = state;
  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);
  const pitchRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const dirty = !sameState(state, initial);

  const commit = useCallback((next: EditorState) => {
    const prev = stateRef.current;
    if (next === prev || sameState(next, prev)) return;
    setPast((p) => [...p.slice(-(MAX_HISTORY - 1)), prev]);
    setState(next);
    setError(null);
  }, []);

  const undo = () => {
    if (past.length === 0) return;
    setState(past[past.length - 1]);
    setPast(past.slice(0, -1));
    setSelected(null);
    setSwapMode(false);
  };

  // --- Nevek és játékos adatok ---
  const nameById = useMemo(() => {
    const map = new Map<number, string>();
    match.squad.forEach((s) => map.set(s.playerId, s.playerName ?? '?'));
    match.lineup.forEach((l) => map.set(l.playerId, l.playerName ?? map.get(l.playerId) ?? '?'));
    selectablePlayers.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [match.squad, match.lineup, selectablePlayers]);

  const playerMap = useMemo(() => {
    const map = new Map<number, PitchPlayer>();
    const ids = new Set<number>([...state.bench, ...state.slots.filter((s) => s.playerId != null).map((s) => s.playerId!)]);
    ids.forEach((id) => map.set(id, { id, name: nameById.get(id) ?? '?', shirtNumber: state.shirts[id] }));
    return map;
  }, [state, nameById]);

  const starters = state.slots.filter((s) => s.playerId != null).length;
  const teamSize = formationSize(state.formation) ?? state.slots.length;

  const shirtCounts = new Map<number, number>();
  playerMap.forEach((p) => {
    if (p.shirtNumber != null) shirtCounts.set(p.shirtNumber, (shirtCounts.get(p.shirtNumber) ?? 0) + 1);
  });
  const duplicateShirts = Array.from(shirtCounts.entries()).filter(([, c]) => c > 1).map(([n]) => n);

  // Mezszám-javaslatok az üres mezszámú nevezetteknek, a már foglalt számok kihagyásával
  const takenShirts = new Set(shirtCounts.keys());
  const fillableShirts = fillShirts(
    Array.from(playerMap.values()).filter((p) => p.shirtNumber == null).map((p) => p.id),
    shirtSuggestions,
    takenShirts
  );
  const handleFillShirts = () => {
    const shirts = { ...stateRef.current.shirts };
    fillableShirts.forEach((number, playerId) => { shirts[playerId] = number; });
    commit({ ...stateRef.current, shirts });
  };

  // --- Pálya méretezése: a rendelkezésre álló helyre a lehető legnagyobb ---
  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      const w = Math.max(160, Math.min(width, (height * 68) / 105));
      setPitchSize({ w, h: (w * 105) / 68 });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- Felállás és létszám ---
  const templateByName = (name: string) => templates.find((t) => t.name === name);

  const changeFormation = (formation: string) => {
    // Saját (mentett) felállás: a sablon helyeire rendezünk
    const template = templateByName(formation);
    const target = template
      ? template.slots.map((slot) => ({ id: newSlotId(), x: slot.x, y: slot.y, role: slot.role }))
      : null;
    const { slots, benched } = target
      ? applySlots(stateRef.current.slots, target)
      : applyFormation(stateRef.current.slots, formation);
    commit({ ...stateRef.current, formation, slots, bench: [...stateRef.current.bench, ...benched] });
    setSelected(null);
    setSwapMode(false);
  };

  // A létszámválasztó a beépített létszámokat és a saját felállások létszámait is mutatja
  const teamSizes = Array.from(new Set([...TEAM_SIZES, ...templates.map((t) => t.teamSize)])).sort((a, b) => b - a);

  const changeTeamSize = (size: number) => {
    if (size === teamSize) return;
    const presets = FORMATIONS[size];
    if (presets) {
      changeFormation(presets.includes(state.formation) ? state.formation : defaultFormationFor(size));
    } else {
      const template = templates.find((t) => t.teamSize === size);
      if (template) changeFormation(template.name);
    }
  };

  const deleteTemplate = async (template: FormationTemplate) => {
    if (!window.confirm(`Törlöd a(z) „${template.name}” felállást? A meccseken már beállított felállásokat nem érinti.`)) return;
    try {
      await formationTemplatesAPI.delete(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    } catch (err) {
      console.error(err);
      setError('Nem sikerült törölni a felállást.');
    }
  };

  // --- Koppintás: kijelölés, majd a második koppintás cserél / áthelyez ---
  const selectedSource = (): Source | null => {
    if (!selected) return null;
    return selected.kind === 'slot' ? { kind: 'slot', slotId: selected.slotId } : { kind: 'bench', playerId: selected.playerId };
  };

  const select = (next: Selected) => {
    setSelected(next);
    setSwapMode(false);
  };

  // A koppintás alapból csak kijelöl (szerkesztéshez); cserélni a "Csere" gombbal
  // vagy húzással lehet, így egy mezszám beírása után nem cserél véletlenül helyet
  const handleSlotClick = (slot: PitchSlot) => {
    if (suppressClickRef.current) return;
    const src = selectedSource();
    if (swapMode && src) {
      if (!(selected?.kind === 'slot' && selected.slotId === slot.id)) commit(placeIntoSlot(state, src, slot.id));
      select(null);
      return;
    }
    if (slot.playerId != null) {
      select(selected?.kind === 'slot' && selected.slotId === slot.id ? null : { kind: 'slot', slotId: slot.id });
    } else {
      select(null);
      setPicker({ slotId: slot.id });
    }
  };

  const handleBenchClick = (playerId: number) => {
    if (suppressClickRef.current) return;
    if (swapMode && selected?.kind === 'slot') {
      commit(placeIntoSlot(state, { kind: 'bench', playerId }, selected.slotId));
      select(null);
      return;
    }
    select(selected?.kind === 'bench' && selected.playerId === playerId ? null : { kind: 'bench', playerId });
  };

  // --- Húzás (egér és érintés) ---
  const resolveDrop = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const target = el?.closest('[data-drop]') as HTMLElement | null;
    return target?.dataset.drop ?? null;
  };

  const pitchPoint = (x: number, y: number) => {
    const rect = pitchRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: ((x - rect.left) / rect.width) * 100, y: ((y - rect.top) / rect.height) * 100 };
  };

  const finishDrop = (drag: DragState, x: number, y: number) => {
    const s = stateRef.current;
    let target = resolveDrop(x, y);
    // A saját helyén belüli finom igazítás is mozgatás, nem csere
    if (drag.source.kind === 'slot' && target === `slot:${drag.source.slotId}`) target = 'pitch';
    if (!target) return;

    if (target.startsWith('slot:')) {
      commit(placeIntoSlot(s, drag.source, target.slice(5)));
    } else if (target === 'bench') {
      if (drag.source.kind !== 'bench') commit(sendToBench(s, drag.playerId));
    } else if (target === 'pitch') {
      const p = pitchPoint(x, y);
      if (!p) return;
      if (drag.source.kind === 'slot') {
        commit(moveSlot(s, drag.source.slotId, p.x, p.y));
      } else {
        const slot = nearestSlot(s.slots, p.x, p.y, true) ?? nearestSlot(s.slots, p.x, p.y, false);
        if (slot) commit(placeIntoSlot(s, drag.source, slot.id));
      }
    }
  };

  const onWindowPointerMove = useCallback((e: PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (!drag.active) {
      if (Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) < DRAG_THRESHOLD) return;
      drag.active = true;
    }
    e.preventDefault();
    setGhost({ playerId: drag.playerId, role: drag.role, x: e.clientX, y: e.clientY });
    let target = resolveDrop(e.clientX, e.clientY);
    if (drag.source.kind === 'slot' && target === `slot:${drag.source.slotId}`) target = 'pitch';
    setDropTarget(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const endDrag = useCallback(
    (e: PointerEvent, cancelled: boolean) => {
      const drag = dragRef.current;
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
      dragRef.current = null;
      setGhost(null);
      setDropTarget(null);
      if (!drag?.active) return;
      // A húzás végén a böngésző még egy kattintást küld - azt elnyeljük
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 0);
      if (!cancelled) {
        finishDrop(drag, e.clientX, e.clientY);
        setSelected(null);
        setSwapMode(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onWindowPointerUp = useCallback((e: PointerEvent) => endDrag(e, false), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onWindowPointerCancel = useCallback((e: PointerEvent) => endDrag(e, true), []);

  const beginDrag = (e: React.PointerEvent, source: Source, playerId: number, role?: string) => {
    if (e.button !== undefined && e.button > 0) return;
    dragRef.current = { source, playerId, role, startX: e.clientX, startY: e.clientY, active: false };
    window.addEventListener('pointermove', onWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerCancel);
  };

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerCancel);
    },
    [onWindowPointerMove, onWindowPointerUp, onWindowPointerCancel]
  );

  // --- Saját felállások ---
  useEffect(() => {
    formationTemplatesAPI
      .getAll()
      .then(setTemplates)
      .catch((err) => console.error('A saját felállások betöltése nem sikerült:', err));
  }, []);

  // --- Billentyűk ---
  useEffect(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName);
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (saveTemplateOpen) setSaveTemplateOpen(false);
      else if (picker) setPicker(null);
      else if (swapMode) setSwapMode(false);
      else if (selected) select(null);
      else handleClose();
    } else if (!typing && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      undo();
    }
  };

  // --- Mentés / bezárás ---
  const handleClose = () => {
    if (dirty && !window.confirm('Elveted a felállás módosításait?')) return;
    onClose();
  };

  const handleSave = async () => {
    if (duplicateShirts.length > 0) {
      setError(`Ez a mezszám többször szerepel: ${duplicateShirts.join(', ')}`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await matchesAPI.setSelection(match.id, {
        formation: state.formation,
        players: [
          ...state.slots
            .filter((s) => s.playerId != null)
            .map((s) => ({
              playerId: s.playerId!,
              role: MatchSelectionRole.Starter,
              shirtNumber: state.shirts[s.playerId!],
              position: s.role,
              pitchX: Math.round(s.x * 100) / 100,
              pitchY: Math.round(s.y * 100) / 100,
            })),
          ...state.bench.map((playerId) => ({
            playerId,
            role: MatchSelectionRole.Sub,
            shirtNumber: state.shirts[playerId],
            position: state.benchRoles[playerId],
          })),
        ],
      });
      onSaved(updated);
      onClose();
    } catch (err) {
      console.error(err);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Nem sikerült menteni a felállást.');
    } finally {
      setSaving(false);
    }
  };

  // --- Kijelölt játékos ---
  const selectedPlayerId =
    selected?.kind === 'slot' ? state.slots.find((s) => s.id === selected.slotId)?.playerId : selected?.kind === 'bench' ? selected.playerId : undefined;
  const selectedSlot = selected?.kind === 'slot' ? state.slots.find((s) => s.id === selected.slotId) : undefined;
  const firstEmptySlot = state.slots.find((s) => s.playerId == null);

  const setShirt = (playerId: number, value: string) => {
    const num = value === '' ? undefined : clamp(parseInt(value, 10) || 1, 1, 99);
    commit({ ...state, shirts: { ...state.shirts, [playerId]: num } });
  };

  const setRole = (slotId: string, role: string) =>
    commit({ ...state, slots: state.slots.map((s) => (s.id === slotId ? { ...s, role } : s)) });

  const setBenchRole = (playerId: number, role: string) =>
    commit({ ...state, benchRoles: { ...state.benchRoles, [playerId]: role || undefined } });

  // Mobilon legalább 16px, különben az iOS ránagyít a mezőre
  const fieldText = 'text-base sm:text-sm';

  const renderSelectedPanel = (compact: boolean) => {
    if (selectedPlayerId == null) return null;
    const name = nameById.get(selectedPlayerId) ?? '?';
    const response = responses.get(selectedPlayerId);
    const badge = response != null ? RESPONSE_BADGES[response] : undefined;
    const shirt = state.shirts[selectedPlayerId];
    const shirtDuplicate = shirt != null && duplicateShirts.includes(shirt);
    const role = selectedSlot ? selectedSlot.role : state.benchRoles[selectedPlayerId] ?? '';
    const onRoleChange = (value: string) =>
      selectedSlot ? setRole(selectedSlot.id, value) : setBenchRole(selectedPlayerId, value);
    const toBench = () => { commit(sendToBench(state, selectedPlayerId)); select(null); };
    const toPitch = () => {
      if (!firstEmptySlot) return;
      commit(placeIntoSlot(state, { kind: 'bench', playerId: selectedPlayerId }, firstEmptySlot.id));
      select(null);
    };
    const remove = () => { commit(removePlayer(state, selectedPlayerId)); select(null); };

    if (swapMode) {
      return (
        <div className="flex items-center gap-3 rounded-lg bg-yellow-50 px-3 py-2.5 ring-1 ring-yellow-200">
          <ArrowsRightLeftIcon className="h-5 w-5 shrink-0 text-yellow-700" />
          <p className="min-w-0 flex-1 text-sm text-yellow-900">
            <span className="font-semibold">{shortName(name)}</span>: koppints arra a helyre vagy játékosra, akivel cseréljen.
          </p>
          <button
            type="button"
            onClick={() => setSwapMode(false)}
            className="shrink-0 rounded-md border border-yellow-300 bg-white px-3 py-1.5 text-sm text-gray-700"
          >
            Mégse
          </button>
        </div>
      );
    }

    const suggested = shirt == null ? suggestShirt(selectedPlayerId, shirtSuggestions, takenShirts) : undefined;
    const shirtInput = (
      <>
      <input
        type="number"
        inputMode="numeric"
        enterKeyHint="done"
        min={1}
        max={99}
        placeholder="#"
        aria-label="Mezszám"
        value={shirt ?? ''}
        onChange={(e) => setShirt(selectedPlayerId, e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`h-10 w-16 shrink-0 rounded-md border px-1 text-center ${fieldText} ${
          shirtDuplicate ? 'border-red-500 bg-red-50' : 'border-gray-300'
        }`}
      />
      {suggested != null && (
        <button
          type="button"
          onClick={() => setShirt(selectedPlayerId, String(suggested))}
          className="ml-2 h-10 shrink-0 rounded-md border border-dashed border-gray-400 px-2 text-sm text-gray-500 hover:border-primary-400 hover:text-primary-700"
          title="Ezt a mezszámot viselte a legutóbbi meccseken – koppints a beírásához"
        >
          #{suggested}
        </button>
      )}
      </>
    );
    const roleSelect = (
      <select
        value={role}
        onChange={(e) => onRoleChange(e.target.value)}
        aria-label="Poszt"
        className={`h-10 min-w-0 flex-1 rounded-md border border-gray-300 px-2 ${fieldText}`}
      >
        {!selectedSlot && <option value="">Nincs poszt</option>}
        {role && !ROLE_LABELS[role] && <option value={role}>{role}</option>}
        {Object.entries(ROLE_LABELS).map(([code, label]) => (
          <option key={code} value={code}>
            {code} – {label}
          </option>
        ))}
      </select>
    );
    const actionBtn = 'inline-flex h-10 items-center justify-center gap-1.5 rounded-md border px-3 text-sm';
    const actions = (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setSwapMode(true)}
          className={`${actionBtn} flex-1 border-gray-300 text-gray-700 hover:bg-gray-50`}
          title="Helycsere egy másik játékossal vagy üres hellyel"
        >
          <ArrowsRightLeftIcon className="h-4 w-4" /> Csere
        </button>
        {selected?.kind === 'slot' ? (
          <button type="button" onClick={toBench} className={`${actionBtn} flex-1 border-gray-300 text-gray-700 hover:bg-gray-50`}>
            <ArrowDownIcon className="h-4 w-4" /> Padra
          </button>
        ) : (
          <button
            type="button"
            onClick={toPitch}
            disabled={!firstEmptySlot}
            title={firstEmptySlot ? undefined : 'Nincs üres hely – használd a Csere gombot'}
            className={`${actionBtn} flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40`}
          >
            <ArrowUpIcon className="h-4 w-4" /> Kezdőbe
          </button>
        )}
        <button
          type="button"
          onClick={remove}
          className={`${actionBtn} shrink-0 border-red-200 text-red-600 hover:bg-red-50`}
          aria-label="Kivesz a keretből"
          title="Kivesz a keretből"
        >
          <UserMinusIcon className="h-4 w-4" />
        </button>
      </div>
    );
    const doneButton = (
      <button
        type="button"
        onClick={() => select(null)}
        className="inline-flex h-10 items-center justify-center rounded-md bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
      >
        Kész
      </button>
    );

    if (compact) {
      // Mobilon tömör elrendezés, hogy a pálya minél nagyobb maradjon
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
              {name}
              {badge && <span className={`ml-1.5 rounded border px-1 py-px text-[10px] font-normal ${badge.className}`}>{badge.label}</span>}
            </p>
            {doneButton}
          </div>
          <div className="flex items-center gap-2">
            {shirtInput}
            {roleSelect}
          </div>
          {actions}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <Jersey number={shirt} keeper={role === 'GK'} className="h-10 w-10 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
            <p className="text-xs text-gray-500">
              {selectedSlot ? 'Kezdő' : 'Cserepad'}
              {badge && <span className={`ml-2 rounded border px-1.5 py-px text-[11px] ${badge.className}`}>{badge.label}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <label className="block">
            <span className="mb-1 block text-xs text-gray-500">Mezszám</span>
            {shirtInput}
          </label>
          <label className="flex min-w-0 flex-1 flex-col">
            <span className="mb-1 block text-xs text-gray-500">Poszt</span>
            {roleSelect}
          </label>
        </div>
        {actions}
        <div className="flex justify-end">{doneButton}</div>
      </div>
    );
  };

  // --- Cserepad ---
  const benchButton = (playerId: number, compact: boolean) => {
    const player = playerMap.get(playerId);
    if (!player) return null;
    const isSelected = selected?.kind === 'bench' && selected.playerId === playerId;
    const dragging = ghost?.playerId === playerId;
    const response = responses.get(playerId);
    const badge = response != null ? RESPONSE_BADGES[response] : undefined;
    return (
      <button
        key={playerId}
        type="button"
        onPointerDown={(e) => beginDrag(e, { kind: 'bench', playerId }, playerId)}
        onClick={() => handleBenchClick(playerId)}
        aria-pressed={isSelected}
        aria-label={`${player.name} (csere)`}
        className={
          compact
            ? `shrink-0 rounded-lg p-1 ${isSelected ? 'bg-yellow-100' : ''} ${dragging ? 'opacity-30' : ''}`
            : `flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${
                isSelected ? 'bg-yellow-100 ring-1 ring-yellow-300' : 'hover:bg-gray-50'
              } ${dragging ? 'opacity-30' : ''}`
        }
        style={{ touchAction: compact ? 'pan-x' : 'pan-y' }}
      >
        {compact ? (
          <div className="rounded-lg bg-gray-800 px-1 pb-1 pt-1.5">
            <PlayerToken player={player} role={state.benchRoles[playerId]} size="sm" selected={isSelected} />
          </div>
        ) : (
          <>
            <Jersey number={player.shirtNumber} keeper={state.benchRoles[playerId] === 'GK'} className="h-8 w-8 shrink-0" />
            <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{player.name}</span>
            {state.benchRoles[playerId] && (
              <span className="shrink-0 rounded bg-gray-900 px-1 text-[10px] font-bold text-white">{state.benchRoles[playerId]}</span>
            )}
            {badge && <span className={`shrink-0 rounded border px-1.5 py-px text-[11px] ${badge.className}`}>{badge.label}</span>}
          </>
        )}
      </button>
    );
  };

  const benchHighlight = dropTarget === 'bench' ? 'ring-2 ring-primary-400 bg-primary-50/60' : '';
  const iconButton =
    'flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent';

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-[60] flex flex-col bg-gray-100 outline-none"
      role="dialog"
      aria-modal="true"
      aria-label="Felállás szerkesztő"
    >
      {/* Fejléc */}
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Felállás</p>
          <p className="truncate text-sm font-semibold text-gray-900">{eventTitle}</p>
        </div>
        <button type="button" onClick={undo} disabled={past.length === 0} className={iconButton} aria-label="Visszavonás" title="Visszavonás (Ctrl+Z)">
          <ArrowUturnLeftIcon className="h-5 w-5" />
        </button>
        <button type="button" onClick={handleClose} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 sm:px-4">
          Mégse
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 sm:px-4"
        >
          {saving ? 'Mentés…' : 'Mentés'}
        </button>
      </div>

      {/* Létszám és felállás */}
      <div className="shrink-0 space-y-2 border-b border-gray-200 bg-white px-3 py-2">
        <div className="flex items-center gap-2 overflow-x-auto">
          <span className="hidden shrink-0 text-xs font-medium text-gray-500 sm:inline">Létszám</span>
          <div className="inline-flex shrink-0 rounded-lg bg-gray-100 p-0.5">
            {teamSizes.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => changeTeamSize(size)}
                aria-pressed={teamSize === size}
                className={`rounded-md px-2.5 py-1 text-sm font-medium ${
                  teamSize === size ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {size}
              </button>
            ))}
          </div>
          <span className="ml-auto shrink-0 text-xs text-gray-500">
            <span className="font-semibold text-gray-900">{starters}</span>/{state.slots.length} kezdő ·{' '}
            <span className="font-semibold text-gray-900">{state.bench.length}</span> csere
          </span>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
          {(FORMATIONS[teamSize] ?? []).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => changeFormation(f)}
              aria-pressed={state.formation === f}
              className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold tabular-nums transition ${
                state.formation === f ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {f}
            </button>
          ))}
          {templates
            .filter((t) => t.teamSize === teamSize)
            .map((t) => {
              const active = state.formation === t.name;
              return (
                <span
                  key={t.id}
                  className={`inline-flex shrink-0 items-center rounded-full text-sm font-semibold transition ${
                    active ? 'bg-primary-600 text-white' : 'bg-primary-50 text-primary-800 ring-1 ring-primary-200 hover:bg-primary-100'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => changeFormation(t.name)}
                    aria-pressed={active}
                    aria-label={`${t.name} (saját felállás)`}
                    title={t.createdByName ? `Saját felállás – ${t.createdByName}` : 'Saját felállás'}
                    className="inline-flex items-center gap-1 py-1 pl-2.5 pr-2"
                  >
                    <BookmarkIcon className="h-3.5 w-3.5" />
                    {t.name}
                  </button>
                  {t.canDelete && (
                    <button
                      type="button"
                      onClick={() => deleteTemplate(t)}
                      aria-label={`${t.name} törlése`}
                      className={`-ml-1 mr-1 rounded-full p-0.5 ${active ? 'hover:bg-primary-700' : 'hover:bg-primary-200'}`}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              );
            })}
          <button
            type="button"
            onClick={() => changeFormation(state.formation)}
            className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs text-gray-500 hover:bg-gray-100"
            title="A játékosok visszarendezése a felállás alaphelyzetébe"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" /> Alaphelyzet
          </button>
          <button
            type="button"
            onClick={() => setSaveTemplateOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50"
            title="A pályán látható elrendezés mentése saját felállásként"
          >
            <BookmarkIcon className="h-3.5 w-3.5" /> Mentés sajátként
          </button>
          {fillableShirts.size > 0 && (
            <button
              type="button"
              onClick={handleFillShirts}
              className="hidden shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 lg:inline-flex"
              title="Az üres mezszámok kitöltése a korosztály korábbi meccsei alapján"
            >
              # Mezszámok kitöltése ({fillableShirts.size})
            </button>
          )}
        </div>
      </div>

      {/* Pálya + oldalsáv */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div ref={areaRef} className="flex min-h-0 flex-1 items-center justify-center p-2 md:p-4">
          <FormationPitch
            pitchRef={pitchRef}
            slots={state.slots}
            players={playerMap}
            interactive
            selectedSlotId={selected?.kind === 'slot' ? selected.slotId : null}
            dropTargetSlotId={dropTarget?.startsWith('slot:') ? dropTarget.slice(5) : null}
            draggingSlotId={ghost && dragRef.current?.source.kind === 'slot' ? dragRef.current.source.slotId : null}
            onSlotPointerDown={(e, slot) => beginDrag(e, { kind: 'slot', slotId: slot.id }, slot.playerId!, slot.role)}
            onSlotClick={handleSlotClick}
            style={pitchSize ? { width: pitchSize.w, height: pitchSize.h } : { width: '100%', maxWidth: 420 }}
            tokenSize={pitchSize && pitchSize.w < 330 ? 'sm' : 'md'}
            className={dropTarget === 'pitch' ? 'ring-4 ring-white/60' : ''}
          />
        </div>

        {/* Asztali oldalsáv */}
        <aside className="hidden w-80 shrink-0 flex-col border-l border-gray-200 bg-white lg:flex">
          {selectedPlayerId != null && <div className="border-b border-gray-200 p-4">{renderSelectedPanel(false)}</div>}
          <div className="flex items-center justify-between px-4 pb-2 pt-4">
            <h3 className="text-sm font-semibold text-gray-900">Cserepad ({state.bench.length})</h3>
            <button
              type="button"
              onClick={() => setPicker({})}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
            >
              <UserPlusIcon className="h-4 w-4" /> Játékos
            </button>
          </div>
          <div data-drop="bench" className={`mx-2 mb-2 min-h-[120px] flex-1 overflow-y-auto rounded-lg p-1 transition ${benchHighlight}`}>
            {state.bench.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-gray-400">Húzz ide játékost a pályáról, vagy adj hozzá újat.</p>
            ) : (
              state.bench.map((id) => benchButton(id, false))
            )}
          </div>
          {selectedPlayerId == null && (
            <p className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
              Húzd a játékost a helyére vagy egy másik játékosra a cseréhez. Koppintásra kijelölöd: ott állíthatod a mezszámot és a posztot.
            </p>
          )}
          {error && <p className="border-t border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
        </aside>

        {/* Mobil alsó sáv: kijelölt játékos + cserepad */}
        <div className="shrink-0 border-t border-gray-200 bg-white pb-safe lg:hidden">
          {error && <p className="bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {selectedPlayerId != null && <div className="border-b border-gray-100 px-3 py-2">{renderSelectedPanel(true)}</div>}
          <div className="flex items-center justify-between px-3 pt-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cserepad ({state.bench.length})</h3>
            {fillableShirts.size > 0 && (
              <button
                type="button"
                onClick={handleFillShirts}
                className="ml-auto mr-1 rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
                title="Az üres mezszámok kitöltése a korosztály korábbi meccsei alapján"
              >
                # Mezszámok ({fillableShirts.size})
              </button>
            )}
            <button
              type="button"
              onClick={() => setPicker({})}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50"
            >
              <UserPlusIcon className="h-4 w-4" /> Játékos
            </button>
          </div>
          <div data-drop="bench" className={`mx-2 mb-2 flex min-h-[76px] gap-1 overflow-x-auto rounded-lg p-1 transition ${benchHighlight}`}>
            {state.bench.length === 0 ? (
              <p className="w-full self-center text-center text-xs text-gray-400">Húzz ide játékost a cserepadra</p>
            ) : (
              state.bench.map((id) => benchButton(id, true))
            )}
          </div>
        </div>
      </div>

      {/* Húzott játékos */}
      {ghost && playerMap.get(ghost.playerId) && (
        <div className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-1/2" style={{ left: ghost.x, top: ghost.y }}>
          <PlayerToken player={playerMap.get(ghost.playerId)!} role={ghost.role} highlighted />
        </div>
      )}

      {saveTemplateOpen && (
        <SaveTemplateDialog
          slots={state.slots}
          // A beépített felállások nevét se lehessen elfoglalni, különben összekeverednének
          existingNames={[...templates.map((t) => t.name), ...Object.values(FORMATIONS).flat()]}
          onClose={() => setSaveTemplateOpen(false)}
          onSaved={(template) => {
            setTemplates((prev) => [...prev, template]);
            // A meccs felállása is ez a név lesz; a helyek maradnak, ahogy most vannak
            commit({ ...stateRef.current, formation: template.name });
            setSaveTemplateOpen(false);
          }}
        />
      )}

      {picker && (
        <PlayerPicker
          state={state}
          slot={picker.slotId ? state.slots.find((s) => s.id === picker.slotId) : undefined}
          eventAgeGroup={eventAgeGroup}
          selectablePlayers={selectablePlayers}
          nameById={nameById}
          responses={responses}
          onPick={(playerId) => {
            const slotId = picker.slotId;
            if (slotId) {
              const source: Source = state.bench.includes(playerId) ? { kind: 'bench', playerId } : { kind: 'pool', playerId };
              commit(placeIntoSlot(state, source, slotId));
              setPicker(null);
            } else {
              commit({ ...state, bench: [...state.bench, playerId] });
            }
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

// --- Saját felállás mentése -------------------------------------------------

interface SaveTemplateDialogProps {
  slots: PitchSlot[];
  existingNames: string[];
  onClose: () => void;
  onSaved: (template: FormationTemplate) => void;
}

const SaveTemplateDialog: React.FC<SaveTemplateDialogProps> = ({ slots, existingNames, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = name.trim();
  const duplicate = existingNames.some((n) => n.toLocaleLowerCase('hu') === trimmed.toLocaleLowerCase('hu'));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed || duplicate) return;
    setSaving(true);
    setError(null);
    try {
      const template = await formationTemplatesAPI.create({
        name: trimmed,
        slots: slots.map((s) => ({ x: Math.round(s.x * 100) / 100, y: Math.round(s.y * 100) / 100, role: s.role })),
      });
      onSaved(template);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Nem sikerült menteni a felállást.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // Mobilon felülre igazítva: alul a megnyíló billentyűzet eltakarná
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose}>
      <form
        onSubmit={handleSave}
        onClick={(e) => e.stopPropagation()}
        className="w-full space-y-4 rounded-b-2xl bg-white p-4 shadow-xl sm:max-w-sm sm:rounded-2xl"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        role="dialog"
        aria-label="Saját felállás mentése"
      >
        <div>
          <p className="text-base font-semibold text-gray-900">Mentés saját felállásként</p>
          <p className="mt-1 text-sm text-gray-500">
            A pályán látható {slots.length} hely a posztokkal együtt mentődik. A klub minden edzője előhívhatja bármelyik
            meccsnél.
          </p>
        </div>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">Név</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={20}
            autoFocus={typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches}
            placeholder="pl. 4-1-2-1-2 gyémánt"
            className={`w-full rounded-md border px-3 py-2 text-base sm:text-sm ${duplicate ? 'border-red-500' : 'border-gray-300'}`}
          />
          {duplicate && <span className="mt-1 block text-xs text-red-600">Ilyen nevű felállás már van – adj neki egyedi nevet.</span>}
        </label>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={!trimmed || duplicate || saving}
            className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Mentés…' : 'Mentés'}
          </button>
        </div>
      </form>
    </div>
  );
};

// --- Játékos választó (új játékos a padra vagy egy üres helyre) -------------

interface PlayerPickerProps {
  state: EditorState;
  slot?: PitchSlot;
  eventAgeGroup: string;
  selectablePlayers: SelectablePlayer[];
  nameById: Map<number, string>;
  responses: Map<number, AttendanceStatus>;
  onPick: (playerId: number) => void;
  onClose: () => void;
}

const PlayerPicker: React.FC<PlayerPickerProps> = ({ state, slot, eventAgeGroup, selectablePlayers, nameById, responses, onPick, onClose }) => {
  const [search, setSearch] = useState('');
  const term = search.trim().toLocaleLowerCase('hu');
  const onPitch = new Set(state.slots.filter((s) => s.playerId != null).map((s) => s.playerId!));
  const nominated = new Set([...Array.from(onPitch), ...state.bench]);
  const matches = (name: string) => term === '' || name.toLocaleLowerCase('hu').includes(term);

  const benchRows = slot ? state.bench.filter((id) => matches(nameById.get(id) ?? '')) : [];
  const poolRows = selectablePlayers
    .filter((p) => !nominated.has(p.id) && matches(p.name))
    // Keresés nélkül csak a saját korosztály látszik, kereséssel a teljes klub
    .filter((p) => term !== '' || p.ageGroups.includes(eventAgeGroup))
    .sort((a, b) => {
      const ra = responses.get(a.id) === AttendanceStatus.Attending ? 0 : 1;
      const rb = responses.get(b.id) === AttendanceStatus.Attending ? 0 : 1;
      return ra - rb || a.name.localeCompare(b.name, 'hu');
    });

  const row = (id: number, name: string, extra?: string) => {
    const response = responses.get(id);
    const badge = response != null ? RESPONSE_BADGES[response] : undefined;
    return (
      <li key={id}>
        <button type="button" onClick={() => onPick(id)} className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50">
          <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{name}</span>
          {extra && <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">{extra}</span>}
          {badge && <span className={`shrink-0 rounded border px-1.5 py-px text-[11px] ${badge.className}`}>{badge.label}</span>}
          <UserPlusIcon className="h-4 w-4 shrink-0 text-primary-600" />
        </button>
      </li>
    );
  };

  return (
    // Mobilon teljes képernyő, a kereső felül: így a billentyűzet csak a lista alját
    // takarhatja, a (kevés) találatot nem
    <div className="fixed inset-0 z-[70] flex items-stretch justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="flex h-full w-full flex-col bg-white shadow-xl sm:h-auto sm:max-h-[85vh] sm:max-w-md sm:rounded-2xl"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Játékos hozzáadása"
      >
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">{slot ? 'Játékos erre a helyre' : 'Játékos a cserepadra'}</p>
            {slot && <p className="text-xs text-gray-500">{ROLE_LABELS[slot.role] ?? slot.role}</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50">
            Kész
          </button>
        </div>
        <div className="border-b px-4 py-2">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              // Érintőképernyőn a billentyűzet ne takarja el rögtön a listát
              autoFocus={typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keresés a klub összes játékosa között…"
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-base sm:text-sm"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto pb-safe">
          {benchRows.length > 0 && (
            <>
              <p className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Cserepadról</p>
              <ul>{benchRows.map((id) => row(id, nameById.get(id) ?? '?'))}</ul>
            </>
          )}
          <p className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {term ? 'Találatok' : `Nem nevezett – ${eventAgeGroup || 'korosztály'}`}
          </p>
          {poolRows.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-500">{term ? 'Nincs találat.' : 'Mindenki nevezve van – keress név szerint más korosztályból.'}</p>
          ) : (
            <ul>
              {poolRows.map((p) => row(p.id, p.name, p.ageGroups.includes(eventAgeGroup) ? undefined : p.ageGroups.join(', ')))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default LineupEditor;
